/** @jsxImportSource @emotion/react */
import { faUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BarcodeFormat } from "@zxing/library";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { checkFormFields } from "../../../../actions/Tenants/config/checkFormFields";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { FieldsToFetchFromItsOwnTable, FormFileSelectFieldReadFormat } from "../../../../actions/Tenants/config/constantsTyped";
import { fruitFormField } from "../../../../actions/Tenants/config/fruitFormFields";
import { layerFormFields } from "../../../../actions/Tenants/config/layerFormFields";
import { MimeType } from "../../../../constants/misc";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../utils";
import FilterableConfigList from "./FilterableConfigList";
import ImageOptionsConfigForm from "./ImageOptionsConfigForm";
import OptionsConfigForm from "./OptionsConfigForm";
import RequiredImageConfigForm from "./RequiredImageConfigForm";
import { yesOrNoRadio } from "./util";

const formTypes = [
    "select-managed",
    "text",
    "textarea",
    "date-radiobutton",
    "number",
    "number-radio",
    "checkbox",
    "checkbox-managed",
    "fruit-images",
    "text-qrcode-scanner",
    "fta-device-field",
    "radio",
    "radio-flag",
    "single-select",
    "radio-managed",
    "layer-select",
    "header",
    "alert",
    "select-creatable",
    "image-radiobutton",
    "layer-select-children",
    "date-picker",
    "file-select",

];


const defaultField = {
    field: {
        label: "Your Label",
        name: "meta_key",
        type: "single-select",
        mandatory: false
    },
    display_filter: {
        fruit_types: [],
        exclude_fruit_types: [],
        layer_types: [],
    }
};


export default function FormConfigForm({ name, title, fields, description, setArray, useDisplayFilter = ["fruit_types", "exclude_fruit_types", "layer_types", "manual_or_with_mini", "meta_conditions"] }) {
    const { tenant_id } = useParams();
    const getFieldTitle = (object) => {
        let name = <span>[{object.name}{object.mandatory ? "*" : ""}] - {object.type}</span>;
        const isManaged = ["select-managed", "checkbox-managed", "radio-managed"].includes(object.type);
        if (isManaged) {
            const isManagedWithRelativeTables = Object.values(FieldsToFetchFromItsOwnTable).includes(object.name);
            if (isManagedWithRelativeTables) {
                name = <Link to={`/admin/sourcing/${object.options_name || object.name}`} target="_blank" rel="noopener noreferrer" >[{object.options_name || object.name}{object.mandatory ? "*" : ""}] - {object.type} <FontAwesomeIcon icon={faUpRightFromSquare} /> </Link>;
            } else {
                name = <Link to={`/tenants/${tenant_id}/form-field-options/${object.options_name || object.name}`} target="_blank" rel="noopener noreferrer" >[{object.options_name || object.name}{object.mandatory ? "*" : ""}] - {object.type} <FontAwesomeIcon icon={faUpRightFromSquare} /> </Link>;
            }
        }

        return <span>{object.label} {name}</span>;
    };
    const config = useConfig();

    const combinedFormFields = useMemo(() => [
        ...fruitFormField.map((item) => ({ value: `fruit__${item.name}`, item, label: `(Fruit Field) ${item.label} [${item.name}]` })),
        ...layerFormFields.map((item) => ({ value: `layer__${item.name}`, item, label: `(Layer Field) ${item.label} [${item.name}] ` })),
        ...checkFormFields.map((item) => ({ value: `check__${item.name}`, item, label: `(Check Field) ${item.label} [${item.name}]` }))
    ], [fruitFormField, layerFormFields, checkFormFields]);


    const getFieldForm = (object, setObject) => {
        const formFieldAttributes = [
            {
                type: "header",
                label: "Select predefined field",
            },
            {
                label: "Predefined field",
                type: "single-select",
                name: "form_field_id",
                description: "Select a predefined field and customize later. This will overwrite the current field config.",
                options: combinedFormFields,
                onchange_callback: ({ value }) => {
                    const predfinedField = combinedFormFields.find((i) => i.value === value);

                    if (!predfinedField) {
                        setObject({ ...object, form_field_id: value });
                        return;
                    }

                    setObject({ form_field_id: value, ...predfinedField.item });

                }
            },
            {
                type: "header",
                label: "Customize field",
            },
            {
                label: "label",
                type: "text",
                description: "What is the label of this input field?",
                name: "label",
                advanced: false,
                display_for: false,
                for_types: false,
                onchange_callback: ({ field, value }) => {
                    setObject({
                        ...object,
                        [field]: value,
                        name: value.toLowerCase().replace(/[^a-z0-9]/g, "_")
                    });
                }
            },
            {
                label: "name",
                description: "What is the name of this input fields? This will be used as the key in the form data and in the database.",
                type: "text",
                name: "name",
                onchange_callback: ({ field, value, setValue }) => setValue(field, value.toLowerCase().replace(/[^a-z0-9]/g, "_")),
                advanced: false,
                display_for: false,
                for_types: false,
            },
            {
                label: "type",
                type: "single-select",
                description: "What is the type of this input fields?",
                name: "type",
                advanced: false,
                display_for: false,
                for_types: false,
                options: formTypes.map((i) => ({ label: i, value: i }))
            },
            {
                label: "color",
                type: "single-select",
                description: "Bootstrap alert color.",
                name: "color",
                for_types: ["alert"],
                options: ["primary", "secondary", "success", "danger", "warning", "info", "light", "dark"].map((color) => ({ label: color, value: color })),
            },
            {
                label: "description",
                type: "textarea",
                description: "Optional text under the alert label.",
                name: "description",
                for_types: ["alert"],
            },
            {
                ...yesOrNoRadio,
                label: "Overwrite barcode template defined in tenant level",
                description: "Overwrite barcode template defined at tenant level in hardcoded config.",
                name: "overwrite_barcode_template",
                advanced: false,
                display_for: false,
                for_types: ["text-qrcode-scanner"],
            },
            {
                label: "Barcode Template",
                type: "text",
                description: "What is the barcode template (regex expression) to eliminate undesired results from the scanner?",
                name: "barcode_template",
                advanced: false,
                display_for: false,
                for_types: ["text-qrcode-scanner"],
            },
            {
                label: "Supported Formats",
                type: "single-select",
                description: "What barcode formats are supported?",
                name: "barcode_formats",
                advanced: false,
                display_for: false,
                for_types: ["text-qrcode-scanner"],
                multi: true,
                return_array: true,
                options: Object.entries(BarcodeFormat).filter(([, value]) => typeof value === "number")
                    .map(([label, value]) => ({ label, value })),
            },
            {
                label: "Data Key",
                type: "text",
                description: "What is the key to get the data from fta response object?",
                name: "data_key",
                advanced: false,
                display_for: false,
                for_types: ["fta-device-field"],

            },
            {
                label: "Data value type",
                type: "single-select",
                description: "What is the type of the data value?",
                name: "data_value_type",
                advanced: false,
                display_for: false,
                for_types: ["fta-device-field"],
                options: [
                    {
                        value: "string",
                        label: "String"
                    },
                    {
                        value: "number",
                        label: "Number"
                    },
                    {
                        value: "boolean",
                        label: "Boolean"
                    }
                ]
            },
            {
                label: "mandatory",
                description: "Is this field mandatory?",
                name: "mandatory",
                advanced: false,
                display_for: false,
                for_types: false,
                ...yesOrNoRadio
            },
            {
                label: "backward",
                type: "number",
                description: "Until how many days back to show dates from?",
                name: "backward",
                advanced: true,
                display_for: false,
                for_types: ["date-radiobutton"],
            },
            {
                label: "forward",
                type: "number",
                description: "How many days forward to show dates from?",
                name: "forward",
                advanced: true,
                display_for: false,
                for_types: ["date-radiobutton"],
            },
            {
                label: "Forbid future dates",
                description: "Should future dates be disabled?",
                name: "disable_future_dates",
                advanced: true,
                display_for: false,
                for_types: ["date-radiobutton", "date"],
                ...yesOrNoRadio,
            },
            {
                label: "min",
                type: "number",
                description: "Minimum value for this input",
                name: "min",
                advanced: true,
                display_for: false,
                for_types: ["number", "number-radio"],
            },
            {
                label: "max",
                type: "number",
                description: "Maximum value for this input",
                name: "max",
                advanced: true,
                display_for: false,
                for_types: ["number", "number-radio"],
            },
            {
                label: "unity",
                type: "text",
                description: "Unity for this input, e.g. KG, LBS or %.",
                name: "unity",
                advanced: true,
                display_for: false,
                for_types: ["number"],
            },
            {
                label: "lg",
                type: "number",
                description: "How many columns should this input take on desktop screen? Fil something between 1 - 12. Only works if using grid form.",
                name: "lg",
                advanced: true,
                display_for: false,
                for_types: false,
                max: 12,
                min: 1
            },
            {
                label: "default_value",
                type: "text",
                description: "What is the default value for this input? Will update the database when form is rendered.",
                name: "default_value",
                advanced: true,
                display_for: false,
                for_types: false,
            },
            {
                label: "default_value_from_layer_key",
                type: "text",
                description: "Will try to read the value from this key of the layer and set it on this form state. E.g. set box_size to copy layer.box_size to check.box_size or intake_yield to copy layer.intake_yield to check.intake_yield. Only works on forms that have implemented this.",
                name: "default_value_from_layer_key",
                advanced: true,
                display_for: false,
                for_types: false,

            },
            {
                label: "default_today",
                description: "For date fields: pre-fill today's date when the form is rendered. The value stays editable.",
                name: "default_today",
                advanced: true,
                display_for: false,
                for_types: ["date-picker"],
                ...yesOrNoRadio
            },
            {
                label: "onchange_callback",
                type: "single-select",
                name: "onchange_callback",
                description: "Do you want to call a function when this input changes? If yes, select the function from the list.",
                advanced: true,
                display_for: false,
                for_types: false,
                options: Object.keys(config.onchange_callbacks).map((i) => ({ label: i, value: i }))
            },
            {
                label: "Inherit ripening classes from fruit config",
                name: "inherit_ripening_classes_from_fruit_config",
                type: "single-select",
                for_types: ["single-select", "radio"],
                options: config.get_fruit_types().flatMap((fruitType) => getFilledArrayOrDefault(fruitType.ripening_fields).map((ripeningField) => ({ label: `Classes from ${fruitType.text} - ${ripeningField.label} [${ripeningField.fieldname_getter}]`, value: ripeningField.fieldname_getter }))),
            },
            {
                type: "element",
                advanced: false,
                display_for: false,
                for_types: ["single-select", "radio", "number-radio"],
                el: <OptionsConfigForm
                    fields={object.options || []}
                    description="Options for dropdown or radio button. Please use one of the `*-managed` types if you have more than 10 options."
                    name="options"
                    useDisplayFilter={false}
                    title="Options"
                    setArray={(options) => setObject({ ...object, options })} />
            },
            {
                type: "element",
                advanced: false,
                display_for: false,
                for_types: ["image-radiobutton"],
                el: <ImageOptionsConfigForm
                    fields={object.options || []}
                    description="Image options for dropdown or radio button."
                    name="image_option"
                    useDisplayFilter={false}
                    title="Image option"
                    setArray={(options) => setObject({ ...object, options })} />
            },
            {
                label: "Image buttons aspect ratio",
                name: "image_buttons_aspect_ratio",
                description: "Aspect ratio for image buttons. If not set, aspect ratio will be automatically detected.",
                type: "single-select",
                for_types: ["image-radiobutton"],
                options: [
                    { label: "Square", value: "square" },
                    { label: "Landscape", value: "landscape" },
                    { label: "Portrait", value: "portrait" }
                ],
            },
            {
                type: "element",
                advanced: false,
                display_for: false,
                for_types: ["fruit-images"],
                el: <RequiredImageConfigForm
                    fields={object.options || []}
                    description="Define which images are required for this fruit. These will be shown in the image upload UI and validated during submission."
                    name="options"
                    useDisplayFilter={["fruit_types", "exclude_fruit_types", "meta_conditions"]}
                    title="Required Images"
                    setArray={(options) => setObject({ ...object, options })} />
            },
            {
                label: "layer_type",
                type: "single-select",
                description: "What layer type to search for",
                name: "layer_type",
                advanced: true,
                display_for: false,
                for_types: ["layer-select", "layer-select-children", "text-qrcode-scanner"],
                options: config.layer_types.map((i) => ({ label: i.text, value: i.value })),
            },
            {
                label: "Disable the button",
                name: "disabled",
                description: "Disable the button if needed",
                advanced: true,
                display_for: false,
                for_types: false,
                ...yesOrNoRadio
            },
            {
                label: "Creatable",
                name: "creatable",
                description: "Is this input creatable? usually used for creating layer if not exist",
                advanced: true,
                display_for: false,
                for_types: false,
                ...yesOrNoRadio
            },
            {
                label: "multi",
                name: "multi",
                advanced: true,
                description: "is this input multi select?",
                display_for: false,
                for_types: ["layer-select", "single-select", "select-managed", "checkbox", "checkbox-managed"],
                ...yesOrNoRadio
            },
            {
                label: "options_name",
                type: "text",
                name: "options_name",
                description: "what is the name to get the options from? For example if you want to store taste (as field name) but the taste options are stored under `taste_avocado`. Fil in `taste_avocado` as `options_name` and `taste` under `name`. Leave blank to just use name to fetch options.",
                advanced: true,
                display_for: false,
                for_types: ["select-managed", "checkbox-managed", "radio-managed"],
            },
            {
                label: "Pattern",
                type: "text",
                name: "pattern",
                description: "Regular expression pattern to validate the input. To let field start with 08 you will need this pattern /^[08]/, therefore fill `^[08]` in this form.",
                advanced: true,
                display_for: false,
                for_types: false,
            },
            {
                label: "invalid_hint",
                type: "text",
                description: "Show a message to the user if the input doesn't match the pattern.",
                name: "invalid_hint",
                advanced: true,
                display_for: false,
                for_types: false,
            },
            // {
            //     label: "image",
            //     type: "text",
            //     name: "image",
            //     advanced: true,
            //     display_for: false,
            // for_types: ["fruit-images"],
            // },
            {
                label: "meta_options",
                type: "single-select",
                name: "meta_options",
                descirption: "Do you want to show meta data in search results?",
                multi: true,
                options: ["box_size", "batch"].map((i) => ({ label: i, value: i })),
                return_array: true,
                advanced: true,
                display_for: false,
                for_types: ["layer-select", "layer-select-children", "text-qrcode-scanner"],
            },
            {
                label: "Defect position",
                name: "defect_position",
                type: "single-select",
                return_array: true,
                multi: true,
                advanced: false,
                display_for: false,
                for_types: ["checkbox-managed"],
                options: config.get_defect_positions().options,
            },
            {
                label: "Defect Severity",
                name: "defect_severity",
                type: "single-select",
                return_array: true,
                multi: true,
                advanced: false,
                for_types: ["checkbox-managed"],
                display_for: false,
                options: config.get_defect_severities().options,
            },
            {
                label: "Show severity on one line",
                name: "defect_severity_on_one_line",
                advanced: false,
                for_types: ["checkbox-managed"],
                display_for: false,
                ...yesOrNoRadio,
            },
            {
                label: "Accept",
                type: "single-select",
                name: "accept",
                description: "What file formats are accepted?",
                advanced: false,
                for_types: ["file-select"],
                multi: true,
                return_array: true,
                options: Object.entries(MimeType).map(([label, value]) => ({ label, value })),
            },
            {
                label: "Read Format",
                type: "single-select",
                name: "readFormat",
                description: "What format to read the file in?",
                advanced: false,
                for_types: ["file-select"],
                display_for: false,
                options: Object.entries(FormFileSelectFieldReadFormat).map(([label, value]) => ({ label, value })),
            }
        ];
        return formFieldAttributes.filter((i) => {
            if (isFilledArray(i.for_types)) {
                return getFilledArrayOrDefault(i.for_types).includes(object.type);
            }
            if (isFilledArray(i.display_for)) {
                return getFilledArrayOrDefault(i.display_for).includes(name);
            }
            return true;
        });
    };
    return <FilterableConfigList
        name={name}
        title={title}
        description={description}
        fields={fields}
        setArray={setArray}
        defaultField={defaultField}
        getFieldForm={getFieldForm}
        getFieldTitle={getFieldTitle}
        useDisplayFilter={useDisplayFilter}
    />;

}


